import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { PublicSessionNativeAppService } from './public-session-signup/public-session-native-app.service';

type DialogScreenProtectionBypassRef = {
  afterOpened: () => Observable<unknown>;
  beforeClosed: () => Observable<unknown>;
  afterClosed: () => Observable<unknown>;
};

type DialogScreenProtectionEnforcementRef = {
  afterClosed: () => Observable<unknown>;
};

@Injectable({
  providedIn: 'root',
})
export class PublicSessionScreenProtectionService {
  private activeScopes = 0;
  private temporaryBypasses = 0;
  private enforcedScopes = 0;
  private nativeReadyListener: EventListener | null = null;
  private syncQueue = Promise.resolve();

  constructor(
    private readonly nativeAppService: PublicSessionNativeAppService,
  ) {}

  activate() {
    this.activeScopes += 1;

    if (this.activeScopes === 1 && typeof window !== 'undefined') {
      this.nativeReadyListener = () => {
        void this.queueSync();
      };
      window.addEventListener('zalamoNativeAppReady', this.nativeReadyListener);
      void this.queueSync(1500);
    } else {
      void this.queueSync();
    }

    let released = false;

    return () => {
      if (released) {
        return;
      }

      released = true;
      this.activeScopes = Math.max(0, this.activeScopes - 1);

      if (this.activeScopes === 0) {
        this.temporaryBypasses = 0;

        if (typeof window !== 'undefined' && this.nativeReadyListener) {
          window.removeEventListener(
            'zalamoNativeAppReady',
            this.nativeReadyListener,
          );
        }

        this.nativeReadyListener = null;
      }
    };
  }

  suspend() {
    this.temporaryBypasses += 1;
    void this.queueSync();

    let released = false;

    return () => {
      if (released) {
        return;
      }

      released = true;
      this.temporaryBypasses = Math.max(0, this.temporaryBypasses - 1);
      void this.queueSync();
    };
  }

  enforce() {
    this.enforcedScopes += 1;
    void this.queueSync();

    let released = false;

    return () => {
      if (released) {
        return;
      }

      released = true;
      this.enforcedScopes = Math.max(0, this.enforcedScopes - 1);
      void this.queueSync();
    };
  }

  suspendWhileDialogOpen(ref: DialogScreenProtectionBypassRef) {
    const subscription = new Subscription();
    let releaseBypass: (() => void) | null = null;
    let cleanedUp = false;

    const release = () => {
      releaseBypass?.();
      releaseBypass = null;
    };
    const cleanup = () => {
      if (cleanedUp) {
        return;
      }

      cleanedUp = true;
      subscription.unsubscribe();
      release();
    };
    const beforeClosed$ = ref.beforeClosed().pipe(first());

    subscription.add(
      ref
        .afterOpened()
        .pipe(first(), takeUntil(beforeClosed$))
        .subscribe(() => {
          if (!cleanedUp) {
            releaseBypass = this.suspend();
          }
        }),
    );
    subscription.add(
      ref
        .beforeClosed()
        .pipe(first())
        .subscribe(() => {
          release();
        }),
    );
    subscription.add(
      ref
        .afterClosed()
        .pipe(first())
        .subscribe(() => {
          cleanup();
        }),
    );

    return cleanup;
  }

  enforceWhileDialogOpen(ref: DialogScreenProtectionEnforcementRef) {
    const release = this.enforce();
    ref
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        release();
      });

    return release;
  }

  private queueSync(waitForBridgeMs = 0) {
    this.syncQueue = this.syncQueue
      .catch(() => undefined)
      .then(() => this.sync(waitForBridgeMs));

    return this.syncQueue;
  }

  private async sync(waitForBridgeMs = 0) {
    if (this.activeScopes === 0) {
      return;
    }

    const nativeReady =
      waitForBridgeMs > 0
        ? await this.nativeAppService.waitForNativeAppBridge(waitForBridgeMs)
        : this.nativeAppService.isLoadedByNativeApp();

    if (this.activeScopes === 0 || !nativeReady) {
      return;
    }

    const shouldEnable =
      this.enforcedScopes > 0 || this.temporaryBypasses === 0;
    if (
      this.nativeAppService.getScreenProtectionState()?.enabled === shouldEnable
    ) {
      return;
    }

    try {
      await this.nativeAppService.setScreenProtection(shouldEnable, {
        timeoutMs: 800,
      });
    } catch {}
  }
}
